import { Facebook, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";
import { useGetCoins } from "@/contexts/GetCoinsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCategoriesQuery } from "@/hooks/queries/useCategoriesQuery";
import { useProvidersQuery } from "@/hooks/queries/useProvidersQuery";
import { useUserInitSocialLinks, useUserInitCMSPages } from "@/hooks/queries/useUserInitQuery";
import { useMemo } from "react";

/** X (formerly Twitter) brand icon */
const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/** TikTok brand icon */
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V9.48a8.18 8.18 0 004.77 1.52V7.56a4.84 4.84 0 01-1-.87z" />
  </svg>
);

export const Footer = () => {
  const { openGetCoinsModal } = useGetCoins();
  const { user } = useAuth();
  const cmsPages = useUserInitCMSPages();
  const { data: categories = [] } = useCategoriesQuery();
  const { data: providers = [] } = useProvidersQuery();
  const socialLinks = useUserInitSocialLinks();
  
  // Get categories with games for Popular Categories section
  const popularCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return categories
      .filter(cat => (cat.game_count || 0) > 0)
      .slice(0, 6) // Limit to 6 categories
      .map(cat => ({
        name: cat.name,
        href: `/game/categories/${cat.slug}`,
      }));
  }, [categories]);
  
  // Group CMS pages by category for different footer sections
  const { policyPages, howToPages } = useMemo(() => {
    const policies: Array<{ name: string; href: string }> = [];
    const howTo: Array<{ name: string; href: string }> = [];
    
    cmsPages.forEach((page) => {
      // If user is logged out, only include pages that don't require login
      if (!user && page.after_login) {
        return;
      }
      
      const link = { name: page.title, href: `/${page.slug}` };
      
      // Map categories to sections
      if (page.category === 'Information') {
        policies.push(link);
      } else if (page.category === 'How To') {
        howTo.push(link);
      }
      // "About Us" and "Explore" categories are being removed
    });
    
    return { policyPages: policies, howToPages: howTo };
  }, [cmsPages, user]);

  // Random 6 game providers from database
  const gameProviders = useMemo(() => {
    if (!providers || providers.length === 0) return [];
    
    // Shuffle and pick 6 random providers
    const shuffled = [...providers].sort(() => Math.random() - 0.5);
    return shuffled
      .slice(0, 6)
      .map(provider => ({
        name: provider,
        href: `/endless-reels?provider=${encodeURIComponent(provider)}`,
      }));
  }, [providers]);

  // Rewards section — hidden on mobile/tablet (accessible via bottom nav)
  const rewardsLinks = [
    { name: "Jackpot", href: "/jackpot" },
    { name: "VIP Loyalty Club", href: "/loyalty-club" },
    { name: "Refer a Friend", href: "/refer-friends" },
    { name: "Promotions", href: "/promotions" },
    { name: "Prizes", href: "/prizes" },
    ...(user ? [{ name: "AMOE", href: "/amoe" }] : []),
  ];

  // Account section (Install App hidden — it's a mobile-only page)
  const accountLinks = [
    { name: "Profile", href: "/profile" },
  ];

  // Help & Learn section (Help Center + How To CMS pages)
  const helpLearnLinks = [
    { name: "Help Center", href: "/help-center" },
    ...howToPages,
  ];

  const footerSections = [
    {
      title: "Popular Categories",
      links: popularCategories,
      color: "text-blue-400",
      hideOnMobile: true,
    },
    {
      title: "Game Providers",
      links: gameProviders,
      color: "text-purple-400",
      hideOnMobile: true,
    },
    {
      title: "Rewards",
      links: rewardsLinks,
      color: "text-emerald-400",
      hideOnMobile: true, // Accessible via bottom nav on mobile/tablet
    },
    {
      title: "Account",
      links: accountLinks,
      color: "text-amber-400",
      hideOnMobile: true,
    },
    {
      title: "Help & Learn",
      links: helpLearnLinks,
      color: "text-red-400",
      hideOnMobile: false,
    },
    {
      title: "Our Policies",
      links: policyPages,
      color: "text-cyan-400",
      hideOnMobile: false,
    },
  ];

  return (
    <footer className="bg-card border-t border-border mt-8 shadow-2xl">
      {/* Desktop/Tablet full footer */}
      <div className="max-w-7xl mx-auto px-4 py-8 hidden lg:block">
        {/* Footer Links Grid */}
        <div className="grid grid-cols-6 gap-8 mb-8">
          {footerSections.map((section) => (
            <div key={section.title} className="space-y-3">
              <h3 className={`font-black text-sm uppercase tracking-wider ${section.color} drop-shadow-sm`}>
                {section.title}
              </h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.name}>
                    {link.href === '/get-coins' ? (
                      <button
                        onClick={openGetCoinsModal}
                        className="text-xs text-muted-foreground hover:text-amber-400 transition-colors text-left hover:translate-x-1 transform duration-200"
                      >
                        {link.name}
                      </button>
                    ) : (
                      <Link 
                        to={link.href}
                        className="text-xs text-muted-foreground hover:text-amber-400 transition-colors hover:translate-x-1 transform duration-200 inline-block"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Legal & Copyright Notice - Desktop */}
        <div className="border-t border-border pt-6 mt-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-2">
              {socialLinks?.social_facebook_url && (
                <a href={socialLinks.social_facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-8 h-8 bg-muted/50 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                  <Facebook className="w-4 h-4 text-muted-foreground" />
                </a>
              )}
              {socialLinks?.social_instagram_url && (
                <a href={socialLinks.social_instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-8 h-8 bg-muted/50 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                  <Instagram className="w-4 h-4 text-muted-foreground" />
                </a>
              )}
              {socialLinks?.social_x_url && (
                <a href={socialLinks.social_x_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on X" className="w-8 h-8 bg-muted/50 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                  <XIcon className="w-4 h-4 text-muted-foreground" />
                </a>
              )}
              {socialLinks?.social_tiktok_url && (
                <a href={socialLinks.social_tiktok_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on TikTok" className="w-8 h-8 bg-muted/50 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                  <TikTokIcon className="w-4 h-4 text-muted-foreground" />
                </a>
              )}
              {socialLinks?.social_youtube_url && (
                <a href={socialLinks.social_youtube_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on YouTube" className="w-8 h-8 bg-muted/50 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                  <Youtube className="w-4 h-4 text-muted-foreground" />
                </a>
              )}
            </div>
            <p className="text-xs text-muted-foreground/70 leading-relaxed text-center">
              © 2026 GoldMachine.com | All Rights Reserved. Participation in sweepstakes promotions on GoldMachine.com is always free—no purchase necessary to enter or win. Void where prohibited by law. GoldMachine.com is owned and operated by Gold Machine LLC, a company incorporated in the State of Delaware, with its registered office located at 2810 N Church St, PMB 642616, Wilmington, Delaware 19802, United States.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet — clean native-app-style footer */}
      <div className="lg:hidden px-5 pt-4 pb-5">
        {/* All links as a centered inline flow with dot separators */}
        <div className="flex flex-wrap justify-center items-center gap-x-1 gap-y-0.5 mb-3">
          {/* Help & Learn links (excluding Help Center on mobile) */}
          {howToPages.map((link, i) => (
            <span key={`help-${link.name}`} className="flex items-center">
              {i > 0 && <span className="text-muted-foreground/40 mx-1">·</span>}
              <Link to={link.href} className="text-[11px] text-muted-foreground active:text-foreground py-1">
                {link.name}
              </Link>
            </span>
          ))}
          {/* Separator between sections */}
          {howToPages.length > 0 && policyPages.length > 0 && (
            <span className="text-muted-foreground/40 mx-1">·</span>
          )}
          {/* Policy links */}
          {policyPages.map((link, i) => (
            <span key={`policy-${link.name}`} className="flex items-center">
              {i > 0 && <span className="text-muted-foreground/40 mx-1">·</span>}
              <Link to={link.href} className="text-[11px] text-muted-foreground active:text-foreground py-1">
                {link.name}
              </Link>
            </span>
          ))}
          {/* AMOE — inline with everything else */}
          {user && (
            <>
              <span className="text-muted-foreground/40 mx-1">·</span>
              <Link to="/amoe" className="text-[11px] text-muted-foreground active:text-foreground py-1">
                AMOE
              </Link>
            </>
          )}
        </div>

        {/* Social icons */}
        <div className="flex justify-center gap-3 mb-3">
          {socialLinks?.social_facebook_url && (
            <a href={socialLinks.social_facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-7 h-7 bg-muted/40 rounded-full flex items-center justify-center active:bg-muted">
              <Facebook className="w-3.5 h-3.5 text-muted-foreground/70" />
            </a>
          )}
          {socialLinks?.social_instagram_url && (
            <a href={socialLinks.social_instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-7 h-7 bg-muted/40 rounded-full flex items-center justify-center active:bg-muted">
              <Instagram className="w-3.5 h-3.5 text-muted-foreground/70" />
            </a>
          )}
          {socialLinks?.social_x_url && (
            <a href={socialLinks.social_x_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on X" className="w-7 h-7 bg-muted/40 rounded-full flex items-center justify-center active:bg-muted">
              <XIcon className="w-3.5 h-3.5 text-muted-foreground/70" />
            </a>
          )}
          {socialLinks?.social_tiktok_url && (
            <a href={socialLinks.social_tiktok_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on TikTok" className="w-7 h-7 bg-muted/40 rounded-full flex items-center justify-center active:bg-muted">
              <TikTokIcon className="w-3.5 h-3.5 text-muted-foreground/70" />
            </a>
          )}
          {socialLinks?.social_youtube_url && (
            <a href={socialLinks.social_youtube_url} target="_blank" rel="noopener noreferrer" aria-label="Follow us on YouTube" className="w-7 h-7 bg-muted/40 rounded-full flex items-center justify-center active:bg-muted">
              <Youtube className="w-3.5 h-3.5 text-muted-foreground/70" />
            </a>
          )}
        </div>

        {/* Company info */}
        <p className="text-[10px] text-muted-foreground/60 leading-relaxed text-center">
          GoldMachine.com is owned and operated by Gold Machine LLC, a company incorporated in the State of Delaware, with its registered office located at 2810 N Church St, PMB 642616, Wilmington, Delaware 19802, United States.
        </p>
        <p className="text-[10px] text-muted-foreground/60 text-center mt-1.5">
          © 2026 GoldMachine.com | All Rights Reserved.
        </p>
      </div>
    </footer>
  );
};
